import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Download } from "lucide-react";

const DISMISS_KEY = "soarlife-install-dismissed";
const DISMISS_DAYS = 7;
const HIDE_PATHS = ["/admin", "/clinic", "/video-visit"];

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Already installed as an app? Never prompt.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;
    // Recently dismissed? Stay quiet for a week.
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 864e5) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Show after a calm delay — Android when the native prompt is captured, iOS always (manual steps)
    const t = setTimeout(() => setVisible(true), 8000);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      clearTimeout(t);
    };
  }, []);

  if (HIDE_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      dismiss();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="install-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:w-[380px] z-[80] bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-3xl shadow-2xl p-5"
        >
          <button
            onClick={dismiss}
            data-testid="install-dismiss"
            aria-label="Dismiss"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#EDEDEE] text-[#3F5043] flex items-center justify-center hover:bg-[#BCB3A6]/40"
          >
            <X size={14} />
          </button>
          <div className="flex items-start gap-4">
            <img
              src="/icons/icon-192.png"
              alt="SoarLife app icon"
              className="w-14 h-14 rounded-2xl shadow-md shrink-0"
            />
            <div className="min-w-0">
              <div className="font-serif italic text-xl text-[#3F5043] leading-tight">
                Add SoarLife to your phone
              </div>
              <p className="text-xs text-[#3F5043]/70 mt-1 leading-relaxed">
                Your roadmap, care team, and video visits — one tap from your home screen. Free, no app store needed.
              </p>
              {isIOS ? (
                <p className="text-xs text-[#875F45] mt-2.5 leading-relaxed" data-testid="install-ios-steps">
                  Tap <Share size={12} className="inline -mt-0.5" /> <strong>Share</strong> in Safari, then{" "}
                  <strong>"Add to Home Screen"</strong>.
                </p>
              ) : (
                <button
                  onClick={install}
                  data-testid="install-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors"
                >
                  <Download size={13} /> {deferred ? "Install the app" : "Install from your browser menu"}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
